/** When the Tally Counter's page-wide space-bar shortcut should add one. */

export interface KeyPress {
  code: string;
  repeat: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
  altKey?: boolean;
}

export interface KeyTarget {
  tagName: string;
  isContentEditable: boolean;
  role?: string | null;
}

/** Elements that use the space bar themselves: typing, choosing, or activating on release. */
const OWN_SPACE_TAGS = new Set([
  "INPUT",
  "TEXTAREA",
  "SELECT",
  "BUTTON",
  "SUMMARY",
  "A",
]);
const OWN_SPACE_ROLES = new Set(["button", "checkbox", "radio", "switch"]);

export function usesSpaceItself(target: KeyTarget | null): boolean {
  if (target === null) {
    return false;
  }
  return (
    OWN_SPACE_TAGS.has(target.tagName.toUpperCase()) ||
    target.isContentEditable ||
    OWN_SPACE_ROLES.has((target.role ?? "").toLowerCase())
  );
}

/**
 * One count per physical press: a held key's auto-repeat is ignored, and so is a press
 * on a focused button or field, which already reacts to the space bar by itself (a button
 * clicks when the key is released), so counting again would double-count.
 */
export function shouldCountOnKey(
  press: KeyPress,
  target: KeyTarget | null
): boolean {
  if (press.code !== "Space" || press.repeat) {
    return false;
  }
  if (press.ctrlKey || press.metaKey || press.altKey) {
    return false;
  }
  return !usesSpaceItself(target);
}
